/**
 * The frame sampler's admission gate: whether it is on, and how often it may
 * open an episode.
 *
 * One input crosses from the app (G1/F5: the package reads no env): `enabled`,
 * set once at boot by `src/composition/wiring/runtime-wiring.ts`, which derives it from
 * `NEXT_PUBLIC_PERF_DEBUG`. The sampler is a developer tool for now — there
 * is no production rollout and no feature flag — so `enabled` is false in
 * every deployed build and the gate refuses everything there. The observer is
 * not even installed in that case (see runtime-wiring), so this refusal is a
 * second line, not the first.
 *
 * The rate limit is package-owned state and is the whole cost bound once
 * enabled: at most one admission every `MIN_ADMISSION_GAP_MS` across every
 * surface, each episode capped at 10 s by the sampler, so the rAF loop runs
 * for a bounded share of any minute; a rolling per-hour ceiling catches a
 * runaway. The perf debug panel lifts both so a developer sees every episode.
 *
 * The gate is consulted once per new episode, never per scroll event or frame.
 * Refusal changes nothing, and an admission is never handed back: a discarded
 * or aborted episode still ran a tracking entry and a rAF loop, so it counts
 * against the work budget exactly like a reported one. Nothing here resets on
 * navigation, remount or visibility: only a fresh document (or the test
 * reset) starts empty.
 */

/** Minimum time between two admissions in one document. */
export const MIN_ADMISSION_GAP_MS = 30_000;

/**
 * The runaway guard is a ROLLING window, not a lifetime count: a desktop
 * document lives for days, and a per-document ceiling at the production gap
 * (200 × 30 s ≈ 100 min) would go dark before lunch and never recover. At
 * the default gap the ceiling is unreachable (120 admissions fit in an hour),
 * so it only bites when the gap has been lowered — which is the runaway it
 * exists for.
 */
export const ADMISSION_WINDOW_MS = 3_600_000;
export const MAX_ADMISSIONS_PER_WINDOW = 200;

let enabled = false;
let minAdmissionGapMs = MIN_ADMISSION_GAP_MS;
let maxAdmissionsPerWindow = MAX_ADMISSIONS_PER_WINDOW;
let lastAdmittedAt = -Infinity;
let windowStartedAt = -Infinity;
let admissionsInWindow = 0;

/**
 * One-time app-bootstrap call (src/composition/wiring/runtime-wiring.ts). `enabled`
 * defaults to false, so a package consumer that never configures the gate
 * samples nothing. The app passes a 0 gap and an unlimited ceiling under the
 * perf debug panel so a developer sees every episode.
 */
export function configureFrameSampler(config: {
  enabled?: boolean;
  minAdmissionGapMs?: number;
  maxAdmissionsPerWindow?: number;
}): void {
  enabled = config.enabled ?? false;
  minAdmissionGapMs = config.minAdmissionGapMs ?? MIN_ADMISSION_GAP_MS;
  maxAdmissionsPerWindow = config.maxAdmissionsPerWindow ?? MAX_ADMISSIONS_PER_WINDOW;
}

/** Whether the app has switched the sampler on for this build. */
export function isFrameSamplerEnabled(): boolean {
  return enabled;
}

/** The window has elapsed: the next admission starts a fresh count. */
const windowExpired = (now: number): boolean =>
  now - windowStartedAt >= ADMISSION_WINDOW_MS;

const admissionsCounted = (now: number): number =>
  windowExpired(now) ? 0 : admissionsInWindow;

const isExhausted = (now: number): boolean =>
  admissionsCounted(now) >= maxAdmissionsPerWindow;

/**
 * The single admission check. `now` is in the `performance.now()` domain.
 * Returns `true` and records the admission, or `false` and records nothing.
 */
export function tryAdmitEpisode(now: number): boolean {
  if (!canAdmitEpisode(now)) return false;
  if (windowExpired(now)) {
    windowStartedAt = now;
    admissionsInWindow = 0;
  }
  lastAdmittedAt = now;
  admissionsInWindow += 1;
  return true;
}

/**
 * Read-only twin of `tryAdmitEpisode`: would an admission succeed right now?
 * The observer asks this BEFORE it walks the DOM for a label or builds a
 * sampler, so a refused event costs a few comparisons and nothing else.
 */
export function canAdmitEpisode(now: number): boolean {
  return enabled && !isExhausted(now) && now - lastAdmittedAt >= minAdmissionGapMs;
}

export type FrameSamplerState = {
  enabled: boolean;
  /** Admissions in the current rolling window. */
  admissions: number;
  /** Milliseconds until the gap allows another admission; 0 when it already does. */
  nextAdmissionInMs: number;
  /** The window's ceiling is reached: nothing is admitted until it rolls over. */
  exhausted: boolean;
  /** Milliseconds until the window rolls over; 0 when no window is open. */
  windowResetsInMs: number;
};

/** Read-only view for the perf debug panel. Never admits or resets. */
export function getFrameSamplerState(now: number): FrameSamplerState {
  return {
    enabled,
    admissions: admissionsCounted(now),
    nextAdmissionInMs: Math.max(0, minAdmissionGapMs - (now - lastAdmittedAt)),
    exhausted: isExhausted(now),
    windowResetsInMs: windowExpired(now)
      ? 0
      : Math.max(0, ADMISSION_WINDOW_MS - (now - windowStartedAt)),
  };
}

export function __resetFrameSamplerForTests(): void {
  enabled = false;
  minAdmissionGapMs = MIN_ADMISSION_GAP_MS;
  maxAdmissionsPerWindow = MAX_ADMISSIONS_PER_WINDOW;
  lastAdmittedAt = -Infinity;
  windowStartedAt = -Infinity;
  admissionsInWindow = 0;
}
